import { useState, useCallback } from 'react';
import axios from 'axios';
import { useLiveRefresh } from '../hooks/useLiveRefresh.js';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8009';

/**
 * StatusView — server-status dashboard for the mobile PWA.
 * Aggregates HavunCore Laravel API + Node.js /health/server endpoints.
 */
export function StatusView({ token }) {
  const [health, setHealth] = useState(null);
  const [serverHealth, setServerHealth] = useState(null);
  const [backupHealth, setBackupHealth] = useState(null);
  const [projects, setProjects] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastCheck, setLastCheck] = useState(null);
  const [refreshFlash, setRefreshFlash] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      setLoading(true);
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const [healthRes, serverRes, backupRes, projectsRes] = await Promise.allSettled([
        axios.get(`${API_URL}/api/health`, { headers }),
        axios.get(`${API_URL}/health/server`),
        axios.get(`${API_URL}/health/backup`),
        axios.get(`${API_URL}/api/projects/status`, { headers }),
      ]);
      setHealth(healthRes.status === 'fulfilled' ? healthRes.value.data : { _error: healthRes.reason?.message || 'Niet bereikbaar' });
      setServerHealth(serverRes.status === 'fulfilled' ? serverRes.value.data : null);
      setBackupHealth(backupRes.status === 'fulfilled' ? backupRes.value.data : null);
      setProjects(projectsRes.status === 'fulfilled' ? projectsRes.value.data : { _error: projectsRes.reason?.message || 'Niet bereikbaar' });

      setError(null);
      setLastCheck(new Date());
      setRefreshFlash(true);
      setTimeout(() => setRefreshFlash(false), 800);
    } catch (err) {
      setError('Kon status niet ophalen');
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Één keer ophalen, daarna alleen op een signaal — `decisions/geen-polling.md`.
  useLiveRefresh(token, fetchStatus);

  if (loading && !health) {
    return <div style={styles.container}><p style={{ color: '#6b7280' }}>Laden...</p></div>;
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>System Status</h2>
        <button onClick={fetchStatus} style={{ ...styles.refreshBtn, ...(refreshFlash ? { background: '#d1fae5', transition: 'background 0.3s' } : { transition: 'background 0.3s' }) }} disabled={loading}>
          {loading ? '⏳' : '🔄'} {refreshFlash ? 'Ververst!' : 'Refresh'}
        </button>
      </div>

      {error && <div style={styles.errorBox}>{error}</div>}

      {/* Server Infrastructure */}
      {serverHealth?.success && (
        <Section title="Server (Hetzner)">
          {Object.entries(serverHealth.services || {}).map(([svc, status]) => (
            <Card key={svc} label={svc} value={status} ok={status === 'active'} warn={status.includes('herstart') || status.includes('instabiel') || status === 'gestopt'} />
          ))}
          {serverHealth.disk && (
            <Card label="Disk" value={`${serverHealth.disk.percent} gebruikt`} ok={parseInt(serverHealth.disk.percent) < 80} warn={parseInt(serverHealth.disk.percent) >= 80} />
          )}
          {serverHealth.memory && (
            <Card label="RAM" value={`${serverHealth.memory.usedMB}/${serverHealth.memory.totalMB} MB`} ok={serverHealth.memory.usedMB < serverHealth.memory.totalMB * 0.85} warn={serverHealth.memory.usedMB >= serverHealth.memory.totalMB * 0.85} />
          )}
          {serverHealth.uptime && (
            <Card label="Uptime" value={serverHealth.uptime.replace('up ', '')} ok />
          )}
        </Section>
      )}

      {/* PM2 Processes */}
      {serverHealth?.pm2?.length > 0 && (
        <Section title="PM2 Processen">
          {serverHealth.pm2.map(p => (
            <Card
              key={p.name}
              label={p.name}
              value={`${p.status} · ${(p.memory / 1024 / 1024).toFixed(0)} MB`}
              ok={p.status === 'online'}
            />
          ))}
        </Section>
      )}

      {/* Backups */}
      {backupHealth?.success && (
        <Section title="Backups">
          <Card
            label="Dagelijks"
            value={backupHealth.latestBackup ? `${backupHealth.backupAgeHours}u geleden` : 'Geen backup'}
            ok={backupHealth.backupAgeHours < 25}
            warn={backupHealth.backupAgeHours >= 25}
          />
          <Card
            label="Hot (5 min)"
            value={backupHealth.hotBackup?.active ? `${backupHealth.hotBackup.ageMinutes} min` : 'Inactief'}
            ok={backupHealth.hotBackup?.active}
            warn={!backupHealth.hotBackup?.active}
          />
          <Card
            label="Hetzner Offsite"
            value={backupHealth.hetznerOffsite ? 'OK' : 'Mislukt'}
            ok={backupHealth.hetznerOffsite}
          />
          {backupHealth.missingDatabases?.length > 0 && (
            <ErrorCard label="Ontbrekende DBs" message={backupHealth.missingDatabases.join(', ')} />
          )}
          {backupHealth.missingDatabases?.length === 0 && (
            <Card
              label="Databases"
              value={`${Object.keys(backupHealth.databases || {}).length} compleet`}
              ok
            />
          )}
        </Section>
      )}

      {/* HavunCore API */}
      <Section title="HavunCore API">
        {health?._error ? (
          <ErrorCard label="Backend" message={health._error} />
        ) : (
          <>
            <Card label="Status" value={health?.status === 'ok' ? 'Online' : 'Offline'} ok={health?.status === 'ok'} />
            <Card label="Versie" value={health?.version || '?'} ok />
          </>
        )}
      </Section>

      {/* Projecten -- álle projecten uit het register. */}
      <Section title="Projecten">
        {projects?._error ? (
          <ErrorCard label="Projectstatus" message={projects._error} />
        ) : (() => {
          const entries = Object.entries(projects?.projects || {});
          if (entries.length === 0) return <Card label="Projecten" value="Geen projecten gevonden" warn />;

          return entries.map(([key, p]) => (
            <Card
              key={key}
              label={p.name || key}
              value={p.status === 'red' ? (p.statusLabel || 'Probleem') : (p.statusLabel || 'OK')}
              ok={p.status === 'green'}
              warn={p.status === 'orange'}
            />
          ));
        })()}
      </Section>

      <div style={styles.footer}>
        Laatste check: {lastCheck ? lastCheck.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—'}
        {' · ververst bij een wijziging'}
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: '24px' }}>
      <h3 style={{ fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '10px' }}>
        {title}
      </h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '10px' }}>
        {children}
      </div>
    </div>
  );
}

function ErrorCard({ label, message }) {
  return (
    <div style={{ gridColumn: '1 / -1', background: '#fee2e2', border: '2px solid #ef4444', borderRadius: '10px', padding: '12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
      <span style={{ fontSize: '1.25rem' }}>❌</span>
      <div>
        <div style={{ fontSize: '0.75rem', fontWeight: '700', color: '#991b1b' }}>{label} — niet bereikbaar</div>
        <div style={{ fontSize: '0.75rem', color: '#b91c1c', marginTop: '2px' }}>{message}</div>
      </div>
    </div>
  );
}

function Card({ label, value, ok, warn, onClick }) {
  const borderColor = ok ? '#10b981' : warn ? '#f59e0b' : '#ef4444';
  const icon = ok ? '✅' : warn ? '⚠️' : '❌';
  return (
    <div
      onClick={onClick}
      style={{
        background: 'white',
        border: `2px solid ${borderColor}`,
        borderRadius: '10px',
        padding: '12px',
        textAlign: 'center',
        ...(onClick ? { cursor: 'pointer', userSelect: 'none' } : {}),
      }}
    >
      <div style={{ fontSize: '1.25rem', marginBottom: '4px' }}>{icon}</div>
      <div style={{ fontSize: '0.7rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
      <div style={{ fontSize: '0.8rem', fontWeight: '600', color: '#111827', marginTop: '2px', wordBreak: 'break-word' }}>{value}</div>
    </div>
  );
}

const styles = {
  container: { padding: '20px', maxWidth: '640px', margin: '0 auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' },
  title: { margin: 0, fontSize: '1.25rem', fontWeight: '700' },
  refreshBtn: { padding: '7px 14px', background: '#f3f4f6', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' },
  errorBox: { background: '#fee2e2', color: '#991b1b', padding: '12px', borderRadius: '8px', marginBottom: '20px', fontSize: '14px' },
  footer: { textAlign: 'center', color: '#9ca3af', fontSize: '0.8rem', marginTop: '8px' },
};
